import { startCase } from "lodash";
import { useCallback } from "react";
import type { UIMatch } from "react-router-dom";

import { useNavTranslation } from "@/hooks/useNavTranslation";

export interface AppRouteHandle {
  breadcrumb?: string | ((data: unknown) => string);
  title?: string | ((data: unknown) => string);
}

/**
 * Title-case a label without flattening acronyms.
 *
 * `startCase(x.toLowerCase())` turned every acronym into a word — "AI Sourcing"
 * rendered as "Ai Sourcing", "NFC" as "Nfc". Words that are already all-caps are
 * left alone; everything else is start-cased as before.
 */
export const humanLabel = (label: string): string =>
  label
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) =>
      word.length <= 4 && word === word.toUpperCase()
        ? word
        : startCase(word.toLowerCase()),
    )
    .join(" ");

/** Matched routes that declare a breadcrumb, outermost first. */
export const breadcrumbMatches = (matches: UIMatch[]): UIMatch[] =>
  matches.filter((match) => (match.handle as AppRouteHandle)?.breadcrumb);

/** A route's breadcrumb label, as declared (English) on the route config. */
export const resolveBreadcrumb = (match: UIMatch): string => {
  const handle = match.handle as AppRouteHandle;
  if (typeof handle?.breadcrumb === "function") {
    return handle.breadcrumb(match.loaderData) ?? "Details";
  }
  return handle?.breadcrumb ?? "Details";
};

/**
 * The page title, as declared (English) on the route config: the deepest
 * matched route with a title, else the last breadcrumb, else `fallback`.
 */
export const resolveRouteTitle = (
  matches: UIMatch[],
  fallback: string,
): string => {
  const titleMatch = [...matches]
    .reverse()
    .find((match) => (match.handle as AppRouteHandle)?.title);

  if (titleMatch) {
    const handle = titleMatch.handle as AppRouteHandle;
    if (typeof handle.title === "function") {
      return handle.title(titleMatch.loaderData) ?? fallback;
    }
    return handle.title ?? fallback;
  }

  const crumbs = breadcrumbMatches(matches);
  if (crumbs.length > 0) return resolveBreadcrumb(crumbs[crumbs.length - 1]);
  return fallback;
};

/**
 * Route titles/breadcrumbs are declared in English in the route configs, so
 * they translate by slug like page tabs. `startCase` is applied only to
 * Latin text — it would corrupt Arabic and is unnecessary there.
 */
export const useLocalizeRouteTitle = () => {
  const { translateTab } = useNavTranslation();
  return useCallback(
    (raw: string): string => {
      const translated = translateTab(raw);
      return translated === raw ? humanLabel(raw) : translated;
    },
    [translateTab],
  );
};
